import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { ElockInfoStatus } from 'sections/LKSection/modules/ELockModule/types'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { OrderStatus } from 'sections/LKSection/modules/OrderModule/types'
import { Margin } from 'components/ui'
import { Info } from 'components/ui/Info/Info'
import { Button, Flex, Paragraph, Text, Title } from 'components/uiKit'
import { getActivateELockRoute, getActivateOrderRoute } from 'utils/helpers/routes/getRoutes'

import { ActivateWithoutELockModal } from '../ActivateWithoutELockModal/ActivateWithoutELockModal'
import { OrderItemStatusHeader } from '../OrderItemStatusHeader/OrderItemStatusHeader'

type OrderStatusRemarkProps = {
  order: Order
}

export const OrderStatusRemark = observer(({ order }: OrderStatusRemarkProps) => {
  const { viewOrderUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isRemarkFromActivate = uiStore.selectedOrder?.status === OrderStatus.remarkFromActivate
  const isRemarkELockStatus = order.eLockInfo?.status === ElockInfoStatus.remark
  const isDeleted = Boolean(order.deleted)

  const activateStepHandler = () => {
    if (order.id) {
      navigate(getActivateOrderRoute(order.id))
    }
  }

  const activateELockStepHandler = () => {
    if (order.id) {
      navigate(getActivateELockRoute(order.id))
    }
  }

  const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

  return (
    <>
      <OrderItemStatusHeader
        type="back"
        title={t(getKey('step'), { count: 2 })}
        subTitle={t(getKey('Необходима доработка'))}
        isActive
      />

      <Margin mt={32}>
        <Flex vertical>
          <Title level={5}>{t(getKey('Необходимо доработать вашу заявку'))}</Title>
          <Margin mt={16}>
            <Paragraph>{t(getKey('Модератор Wildberries'))}</Paragraph>
          </Margin>

          {uiStore.lastComment?.comment.length && (
            <Margin mb={16}>
              <Info type="corp" disableMargin>
                <Text type="secondary">{t(getKey('Комментарий модератора'))}:</Text>
                <Paragraph>{uiStore.lastComment.comment}</Paragraph>
              </Info>
            </Margin>
          )}

          <Margin mb={16}>
            <Title level={5}>{t(getKey('Что дальше?'))}</Title>
          </Margin>

          <Paragraph>{t(getKey('Внесите необходимые правки'))}</Paragraph>

          {isRemarkFromActivate &&
            (isRemarkELockStatus ? (
              <Flex vertical gap={8}>
                {order.eLockInfo?.elockName && order.eLockInfo.enabled ? (
                  <Button onClick={activateELockStepHandler} disabled={isDeleted}>
                    {t(getKey('Доработать заявку с активацией замка'))}
                  </Button>
                ) : (
                  <Button onClick={() => setIsModalOpen(true)} type="default" disabled={isDeleted}>
                    {t(getKey('Доработать заявку без активации замка'))}
                  </Button>
                )}
              </Flex>
            ) : (
              <Button onClick={activateStepHandler} disabled={isDeleted}>
                {t(getKey('Доработать заявку'))}
              </Button>
            ))}
        </Flex>
      </Margin>

      {order.id && <ActivateWithoutELockModal orderId={order.id} open={isModalOpen} setIsOpen={setIsModalOpen} />}
    </>
  )
})
